import { ReactNode, useMemo } from 'react';
import { useBottomSheet } from '@croquiscom-pvt/zds';

import { displayPrice } from '@common/utils';
import {
  PdpShippingFeeType,
  PopupViewType,
  TraitType,
  useCatalogProductIdParams,
  usePdpBaseInfo,
  usePopupView,
} from '@domains/pdp-common';

import { useShippingUBL } from '../log/useShippingUBL';

export const useShippingGeneralProductService = () => {
  const { shippingInfoClickUBL } = useShippingUBL();
  const { showPopup } = usePopupView();
  const { open: openBottomSheet } = useBottomSheet('custom');

  const params = useCatalogProductIdParams();
  const { product } = usePdpBaseInfo(params);

  const estimated_shipping_date = useMemo(() => {
    if (product.estimated_shipping_date) {
      return product.estimated_shipping_date;
    }
    return {
      estimate_list: null,
      repr_estimate: null,
    };
  }, [product]);

  const max_shipping_days = 4;

  const shipping_details = useMemo(() => {
    return {
      shipping_fee: product.shipping_fee?.base_fee ?? 0,
      shipping_fee_type: product.shipping_fee?.fee_type ?? PdpShippingFeeType.CHARGED,
      free_shipping_amount: product.shipping_fee?.minimum_free_shipping_fee ?? null,
      shipping_company: product?.shipping_company?.base_company ?? null,
      additional_shipping_fee_text: product?.shipping_fee?.additional_shipping_fee_text ?? null,
    };
  }, [product.shipping_fee?.fee_type]);

  const getShippingFeeText = (shipping_fee: number | null) => {
    if (shipping_fee === null) {
      return '정보없음';
    }
    if (shipping_fee === 0 || shipping_details.shipping_fee_type === PdpShippingFeeType.FREE) {
      return '무료배송';
    }
    return displayPrice(shipping_fee);
  };

  const handleInfoIconClick = (children: ReactNode) => {
    showPopup(PopupViewType.SHIPPING, () => {
      shippingInfoClickUBL(product?.id);
      shipping_details &&
        openBottomSheet({
          children,
        });
    });
  };

  return {
    getShippingFeeText,
    handleInfoIconClick,
    shipping_details,
    estimated_shipping_date,
    shipping_send_date: product.shipping_send_date,
    show_free_shipping_amount:
      Boolean(shipping_details.free_shipping_amount) && shipping_details.shipping_fee_type !== PdpShippingFeeType.FREE,
    is_over_max_days: estimated_shipping_date && estimated_shipping_date?.repr_estimate?.day === max_shipping_days,
    isFastShipping: Boolean(product.shipping_send_date?.fast_shipping?.is_fast_type),
    fast_guide_text: product.shipping_send_date?.guide_text ?? null,
    item_guide_text: product.shipping_send_date?.item_guide_text ?? null,
    type_text: product.shipping_send_date?.fast_shipping?.type_text ?? '',
    isOverseasPurchase: Boolean(
      product.trait_list?.find(
        (o) => o.type === TraitType.OVERSEAS_PURCHASE_AGENCY || o.type === TraitType.OVERSEAS_DIRECT_PURCHASE,
      ),
    ),
  };
};
